import {
  Autocomplete,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { Can } from 'context';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteBetLimit, getPresets, getNhRoomsForBetLimit, stopNhRoom, updateBetLimit, getNhExchangeRates, startNhEngine, stopNhEngine, getNhEngineStatus } from 'services/games';
import SimulateBlockMined from './SimulateBlockMined';

const darkFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.06)',
    '& input, & textarea': {
      color: '#d3ddf5',
      WebkitTextFillColor: '#d3ddf5'
    },
    '& .MuiSelect-select': {
      color: '#d3ddf5',
      WebkitTextFillColor: '#d3ddf5'
    },
    '& .MuiSvgIcon-root': { color: 'rgba(211,221,245,0.82)' },
    '& fieldset': { borderColor: 'rgba(34,211,238,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(34,211,238,0.45)' },
    '&.Mui-focused fieldset': { borderColor: '#22d3ee' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(223,232,250,0.88)' },
  '& .MuiInputLabel-root.MuiInputLabel-shrink': { color: 'rgba(223,232,250,0.82)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#22d3ee' },
  '& .MuiInputBase-input': {
    color: '#e6eefc',
    WebkitTextFillColor: '#e6eefc'
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(223,232,250,0.62)',
    opacity: 1
  },
  '& .MuiFormHelperText-root, & .MuiFormHelperText-root.MuiFormHelperText-root': {
    color: '#9fd3ff',
    marginLeft: 0,
    marginRight: 0
  },
  '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
    color: 'rgba(211,221,245,0.82)'
  }
};

export default function BetLimit({
  id,
  gameType,
  minBet,
  maxBet,
  gameId,
  singleBet,
  initialAirdropPercentage,
  initialJackpotPercentage,
  initialFirstPlacePercentage,
  initialSecondPlacePercentage,
  initialThirdPlacePercentage,
  initialBossPercentage,
  initialThresholdMultiplier,
  initialAirdropMeterThreshold,
  initialAirdropMinimumPot,
  initialPreset,
  initialPresetMinAmount,
  initialAddresses,
  initialEnabled,
  initialRequireAccessCode,
  initialShowInCryptoExpressLobby,
  initialAutoBidIntervalMs
}) {
  const [editBetLimitMin, setEditBetLimitMin] = useState(minBet);
  const [editBetLimitMax, setEditBetLimitMax] = useState(maxBet);
  const [editBetLimitSingle, setEditBetLimitSingle] = useState(singleBet);
  const [enabled, setEnabled] = useState(initialEnabled !== false);
  const [requireAccessCode, setRequireAccessCode] = useState(!!initialRequireAccessCode);
  const [showInCryptoExpressLobby, setShowInCryptoExpressLobby] = useState(
    initialShowInCryptoExpressLobby !== false
  );

  const [airdropPercentage, setAirdropPercentage] = useState(initialAirdropPercentage);
  const [jackpotPercentage, setJackpotPercentage] = useState(initialJackpotPercentage);
  const [firstPlacePercentage, setFirstPlacePercentage] = useState(initialFirstPlacePercentage);
  const [secondPlacePercentage, setSecondPlacePercentage] = useState(initialSecondPlacePercentage);
  const [thirdPlacePercentage, setThirdPlacePercentage] = useState(initialThirdPlacePercentage);
  const [bossPercentage, setBossPercentage] = useState(initialBossPercentage);
  const [thresholdMultiplier, setThresholdMultiplier] = useState(initialThresholdMultiplier);
  const [airdropMeterThreshold, setAirdropMeterThreshold] = useState(initialAirdropMeterThreshold);
  const [airdropMinimumPot, setAirdropMinimumPot] = useState(initialAirdropMinimumPot);
  const [autoBidIntervalMs, setAutoBidIntervalMs] = useState(initialAutoBidIntervalMs);

  const [preset, setPreset] = useState(initialPreset);
  const [presetMinAmount, setPresetMinAmount] = useState(initialPresetMinAmount);
  const [addresses, setAddresses] = useState(initialAddresses);
  const [presetOptions, setPresetOptions] = useState([]);

  const [openDeleteBetLimit, setOpenDeleteBetLimit] = useState();
  const handleOpenDeleteBetLimit = () => setOpenDeleteBetLimit(true);
  const handleCloseDeleteBetLimit = () => setOpenDeleteBetLimit(false);

  const [errorMessage, setErrorMessage] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [updated, setUpdated] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomsError, setRoomsError] = useState('');
  const [stopTarget, setStopTarget] = useState(null);
  const [exchangeData, setExchangeData] = useState(null);
  const [engineStatus, setEngineStatus] = useState(null);
  const [engineLoading, setEngineLoading] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [engineMessage, setEngineMessage] = useState('');
  const [engineMsgType, setEngineMsgType] = useState('info');
  const navigate = useNavigate();

  const getRequestErrorMessage = (err, fallbackMessage) => {
    const message = err?.response?.data?.message;
    if (Array.isArray(message)) {
      return message.join(', ');
    }
    if (typeof message === 'string' && message.trim() !== '') {
      return message;
    }
    if (typeof err?.message === 'string' && err.message.trim() !== '') {
      return err.message;
    }
    return fallbackMessage;
  };

  useEffect(() => {
    setTimeout(() => setUpdated(false), 5000);
  }, [updated]);

  useEffect(() => {
    const fetchPresets = async () => {
      const presets = await getPresets();
      setPresetOptions(presets.data);
    };
    fetchPresets();
  }, []);

  useEffect(() => {
    if (gameType !== 'nicehash-game') return;
    getNhExchangeRates().then(setExchangeData);
  }, [gameType]);

  const refreshEngineStatus = async () => {
    if (gameType !== 'nicehash-game') return;
    try {
      const status = await getNhEngineStatus(id);
      setEngineStatus(status);
    } catch {
      setEngineStatus(null);
    }
  };

  useEffect(() => {
    refreshEngineStatus();
  }, [id, gameType]);

  // F6 fix: Auto-clear engine message after 8 seconds
  useEffect(() => {
    if (!engineMessage) return;
    const timer = setTimeout(() => setEngineMessage(''), 8000);
    return () => clearTimeout(timer);
  }, [engineMessage]);

  const handleStartEngine = async () => {
    const confirmed = window.confirm('Start mining engine for this bet limit?');
    if (!confirmed) return;
    setEngineLoading(true);
    setEngineMessage('');
    try {
      const res = await startNhEngine(id);
      if (res.alreadyRunning) {
        setEngineMsgType('info');
        setEngineMessage(`Engine already running (${res.engineId})`);
      } else {
        setEngineMsgType('success');
        const suffix = res.balanceCheckSkipped ? ' (balance check skipped)' : '';
        setEngineMessage(`Engine started: ${res.engineId}${suffix}`);
      }
      await refreshEngineStatus();
      await refreshRooms();
    } catch (err) {
      setEngineMsgType('error');
      setEngineMessage(`Failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setEngineLoading(false);
    }
  };

  const handleStopEngine = async () => {
    const confirmed = window.confirm('Stop ALL engines for this bet limit? This will disconnect any active players.');
    if (!confirmed) return;
    setEngineLoading(true);
    setEngineMessage('');
    try {
      const res = await stopNhEngine(id);
      const orphanMsg = res.orphansStopped ? ` (+${res.orphansStopped} orphaned)` : '';
      setEngineMsgType('success');
      setEngineMessage(`Stopped ${res.stopped}/${res.total || 0} engine(s)${orphanMsg}`);
      await refreshEngineStatus();
      await refreshRooms();
    } catch (err) {
      setEngineMsgType('error');
      setEngineMessage(`Failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setEngineLoading(false);
    }
  };

  const refreshRooms = async () => {
    if (gameType !== 'nicehash-game') return;
    setRoomsLoading(true);
    setRoomsError('');
    try {
      const res = await getNhRoomsForBetLimit(id);
      setRooms(res || []);
    } catch (err) {
      setRoomsError('Failed to load rooms. Please retry.');
    } finally {
      setRoomsLoading(false);
    }
  };

  useEffect(() => {
    refreshRooms();
  }, [id, gameType]);

  useEffect(() => {
    const selected = presetOptions.find((p) => p.name === preset);
    if (selected) {
      setPresetMinAmount(selected.minAmount);
      const newAddresses = {};
      selected.addresses.forEach((addr) => {
        newAddresses[addr] = '';
      });
      setAddresses(newAddresses);
    }
  }, [preset]);

  const percentageSum = useMemo(() => {
    return (
      (Number(firstPlacePercentage) || 0) +
      (Number(secondPlacePercentage) || 0) +
      (Number(thirdPlacePercentage) || 0) +
      (Number(bossPercentage) || 0) +
      (Number(airdropPercentage) || 0) +
      (Number(jackpotPercentage) || 0)
    );
  }, [firstPlacePercentage, secondPlacePercentage, thirdPlacePercentage, bossPercentage, airdropPercentage, jackpotPercentage]);

  const percentageSumValid = Math.abs(percentageSum - 100) <= 0.01;

  const updateBetLimitBtn = async () => {
    const dto = {
      gameId: Number(gameId),
      singleBet: editBetLimitSingle ? Number(editBetLimitSingle) : editBetLimitSingle,
      airdropPercentage: airdropPercentage != null ? Number(airdropPercentage) : airdropPercentage,
      jackpotPercentage: jackpotPercentage != null ? Number(jackpotPercentage) : jackpotPercentage,
      firstPlacePercentage: firstPlacePercentage != null ? Number(firstPlacePercentage) : firstPlacePercentage,
      secondPlacePercentage: secondPlacePercentage != null ? Number(secondPlacePercentage) : secondPlacePercentage,
      thirdPlacePercentage: thirdPlacePercentage != null ? Number(thirdPlacePercentage) : thirdPlacePercentage,
      bossPercentage: bossPercentage != null ? Number(bossPercentage) : bossPercentage,
      thresholdMultiplier: thresholdMultiplier ? Number(thresholdMultiplier) : thresholdMultiplier,
      airdropMeterThreshold: airdropMeterThreshold ? Number(airdropMeterThreshold) : airdropMeterThreshold,
      airdropMinimumPot: airdropMinimumPot != null ? Number(airdropMinimumPot) : airdropMinimumPot,
      autoBidIntervalMs: autoBidIntervalMs != null ? Number(autoBidIntervalMs) : autoBidIntervalMs,
      preset,
      presetMinAmount: presetMinAmount ? Number(presetMinAmount) : presetMinAmount,
      addresses,
      requireAccessCode
    };
    if (gameType === 'nicehash-game' && singleBet) {
      dto.showInCryptoExpressLobby = showInCryptoExpressLobby;
    }

    if (addresses) {
      if (Object.keys(addresses).length !== Object.values(addresses).filter((v) => v != null && v != '').length) {
        setErrorMessage(true);
        return;
      }
    }
    if (minBet) dto.minBet = editBetLimitMin ? Number(editBetLimitMin) : editBetLimitMin;
    if (maxBet) dto.maxBet = editBetLimitMax ? Number(editBetLimitMax) : editBetLimitMax;
    setRequestError('');
    try {
      await updateBetLimit(id, dto);
      setUpdated(true);
    } catch (err) {
      setUpdated(false);
      setRequestError(getRequestErrorMessage(err, 'Failed to update bet limit.'));
    }
  };

  const toggleEnabledBtn = async () => {
    const nextEnabled = !enabled;
    setToggleLoading(true);
    setRequestError('');
    try {
      await updateBetLimit(id, { enabled: nextEnabled });
      setEnabled(nextEnabled);
      setUpdated(true);
    } catch (err) {
      setUpdated(false);
      setRequestError(getRequestErrorMessage(err, 'Failed to update bet limit status.'));
    } finally {
      setToggleLoading(false);
    }
  };

  const handleConfirmDeleteBetLimit = async () => {
    setDeleteLoading(true);
    setRequestError('');
    try {
      await deleteBetLimit(id);
      handleCloseDeleteBetLimit();
      navigate(0);
    } catch (err) {
      setRequestError(getRequestErrorMessage(err, 'Failed to delete bet limit.'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleStopRoom = async (roomId) => {
    const confirmed = window.confirm('Are you sure you want to stop this room?');
    if (!confirmed) return;
    setStopTarget(roomId);
    try {
      await stopNhRoom(roomId);
      await refreshRooms();
    } catch (err) {
      setRoomsError('Failed to stop room. Please try again.');
    } finally {
      setStopTarget(null);
    }
  };

  return (
    <>
      <MDBox
        sx={{
          border: '1px solid rgba(90,163,255,0.2)',
          borderRadius: '8px',
          padding: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          width: { xs: '100%', sm: 'calc(50% - 10px)', lg: '23%' },
          background: 'linear-gradient(160deg, #111d38 0%, #162848 55%, #0e1a2e 100%)'
        }}
      >
        {minBet && (
          <TextField
            label='Min Bet'
            value={editBetLimitMin}
            onChange={(e) => setEditBetLimitMin(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        {maxBet && (
          <TextField
            label='Max Bet'
            value={editBetLimitMax}
            onChange={(e) => setEditBetLimitMax(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        {singleBet && (
          <TextField
            label='Single Bet'
            value={editBetLimitSingle}
            onChange={(e) => setEditBetLimitSingle(e.target.value)}
            type='number'
            fullWidth
            variant='outlined'
            sx={darkFieldSx}
          />
        )}
        <FormControlLabel
          sx={{ '& .MuiFormControlLabel-label': { color: '#d3ddf5' } }}
          control={
            <Checkbox
              checked={requireAccessCode}
              onChange={(e) => setRequireAccessCode(e.target.checked)}
              sx={{ color: 'rgba(211,221,245,0.5)', '&.Mui-checked': { color: '#22d3ee' } }}
            />
          }
          label='Require access code'
        />
        <MDBox
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 12px',
            borderRadius: '8px',
            border: `1px solid ${enabled ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)'}`,
            background: enabled ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)'
          }}
        >
          <MDTypography
            sx={{
              fontSize: '13px',
              fontWeight: 700,
              color: enabled ? '#22c55e' : '#ef4444'
            }}
          >
            {enabled ? 'Active' : 'Inactive'}
          </MDTypography>
          <MDButton
            color={enabled ? 'error' : 'success'}
            size='small'
            disabled={toggleLoading}
            onClick={toggleEnabledBtn}
          >
            {toggleLoading ? 'Working...' : enabled ? 'Disable' : 'Enable'}
          </MDButton>
        </MDBox>
        {gameType === 'nicehash-game' && singleBet && (
          <FormControlLabel
            sx={{ '& .MuiFormControlLabel-label': { color: '#d3ddf5' } }}
            control={
              <Checkbox
                checked={showInCryptoExpressLobby}
                onChange={(e) => setShowInCryptoExpressLobby(e.target.checked)}
                sx={{ color: 'rgba(211,221,245,0.5)', '&.Mui-checked': { color: '#22d3ee' } }}
              />
            }
            label='Show in Crypto Express lobby'
          />
        )}
        {gameType === 'nicehash-game' && (
          <>
            <TextField
              label='Airdrop %'
              value={airdropPercentage}
              onChange={(e) => setAirdropPercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='Jackpot %'
              value={jackpotPercentage}
              onChange={(e) => setJackpotPercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='First place %'
              value={firstPlacePercentage}
              onChange={(e) => setFirstPlacePercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='Second place %'
              value={secondPlacePercentage}
              onChange={(e) => setSecondPlacePercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='Third place %'
              value={thirdPlacePercentage}
              onChange={(e) => setThirdPlacePercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='Boss %'
              value={bossPercentage}
              onChange={(e) => setBossPercentage(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <MDBox
              sx={{
                padding: '8px 12px',
                borderRadius: '6px',
                background: percentageSumValid ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)',
                border: `1px solid ${percentageSumValid ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
              }}
            >
              <MDTypography
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: percentageSumValid ? '#22c55e' : '#ef4444',
                  textAlign: 'center',
                }}
              >
                Total: {percentageSum}% {percentageSumValid ? '✓' : '— must equal 100%'}
              </MDTypography>
            </MDBox>

            <TextField
              label='Threshold Multiplier'
              value={thresholdMultiplier}
              onChange={(e) => setThresholdMultiplier(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />

            <TextField
              label='Airdrop Meter Threshold ($)'
              value={airdropMeterThreshold}
              onChange={(e) => setAirdropMeterThreshold(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
              helperText='USD bet amount that triggers airdrop distribution (default: 500)'
            />

            <TextField
              label='Airdrop Minimum Pot (BTC)'
              value={airdropMinimumPot}
              onChange={(e) => setAirdropMinimumPot(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
              helperText='Minimum engine airdrop pot before meter starts filling (0 = no gate)'
            />

            <TextField
              label='Auto-Bid Interval (ms)'
              value={autoBidIntervalMs}
              onChange={(e) => setAutoBidIntervalMs(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
              helperText='Interval between auto-bid ticks in milliseconds (2000–30000, default: 2000)'
            />

            <Autocomplete
              options={presetOptions}
              disableClearable
              getOptionLabel={(option) => `${option.name} - ${option.description}`}
              value={presetOptions.find((p) => p.name === preset) || null}
              onChange={(event, newValue) => setPreset(newValue ? newValue.name : '')}
              renderInput={(params) => <TextField {...params} label='Select a Preset' fullWidth variant='outlined' sx={darkFieldSx} />}
            />

            <TextField
              label='Preset Min Amount'
              value={presetMinAmount}
              onChange={(e) => setPresetMinAmount(e.target.value)}
              type='number'
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />
          </>
        )}
        {gameType === 'nicehash-game' &&
          Object.entries(addresses || {}).map(([key, val]) => (
            <TextField
              key={key}
              label={`${key} Address`}
              value={val}
              onChange={(e) =>
                setAddresses((prev) => ({
                  ...prev,
                  [key]: e.target.value
                }))
              }
              fullWidth
              variant='outlined'
              sx={darkFieldSx}
            />
          ))}
        {gameType === 'nicehash-game' && (
          <Can I='manage' a='all'>
            <MDBox
              sx={{
                border: '1px solid #ffffff3b',
                borderRadius: '8px',
                padding: '10px',
                background: '#0f1a34',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <MDBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <MDTypography sx={{ fontSize: '14px', fontWeight: 600, color: '#dbe8ff' }}>Engine</MDTypography>
                <MDBox
                  sx={{
                    px: 1.2,
                    py: 0.3,
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: 700,
                    color: '#fff',
                    background: engineStatus?.hasActiveEngine ? '#16a34a' : '#64748b',
                  }}
                >
                  {engineStatus?.hasActiveEngine ? 'ACTIVE' : 'STOPPED'}
                </MDBox>
              </MDBox>
              <MDBox sx={{ display: 'flex', gap: '6px' }}>
                <MDButton
                  color='success'
                  size='small'
                  fullWidth
                  disabled={engineLoading || engineStatus?.hasActiveEngine}
                  onClick={handleStartEngine}
                >
                  {engineLoading ? 'Working...' : 'Start Engine'}
                </MDButton>
                <MDButton
                  color='error'
                  size='small'
                  fullWidth
                  disabled={engineLoading || (!engineStatus?.hasActiveEngine && !(engineStatus?.orphanedEngines > 0))}
                  onClick={handleStopEngine}
                >
                  {engineLoading ? 'Working...' : 'Stop Engine'}
                </MDButton>
              </MDBox>
              {engineStatus?.orphanedEngines > 0 && (
                <MDTypography sx={{ fontSize: '11px', color: '#fbbf24', textAlign: 'center' }}>
                  {engineStatus.orphanedEngines} orphaned engine(s) detected — use Stop Engine to clean up
                </MDTypography>
              )}
              {engineMessage && (
                <MDTypography
                  sx={{
                    fontSize: '11px',
                    textAlign: 'center',
                    color: engineMsgType === 'error' ? '#ff8a80' : engineMsgType === 'success' ? '#81c784' : '#9bc2ff',
                  }}
                >
                  {engineMessage}
                </MDTypography>
              )}
            </MDBox>

            <MDBox
              sx={{
                border: '1px solid #ffffff3b',
                borderRadius: '8px',
                padding: '10px',
                background: '#0f1a34',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <MDBox sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <MDTypography sx={{ fontSize: '14px', fontWeight: 600, color: '#dbe8ff' }}>Rooms</MDTypography>
                <MDButton color='info' size='small' variant='outlined' disabled={roomsLoading} onClick={refreshRooms}>
                  Refresh
                </MDButton>
              </MDBox>
              {roomsError && <MDTypography sx={{ fontSize: '12px', color: '#ff8a80' }}>{roomsError}</MDTypography>}
              {roomsLoading && <MDTypography sx={{ fontSize: '12px', color: '#fff' }}>Loading rooms...</MDTypography>}
              {!roomsLoading && !rooms?.length && (
                <MDTypography sx={{ fontSize: '12px', color: '#fff' }}>No active rooms</MDTypography>
              )}
              {!roomsLoading &&
                rooms?.map((room) => (
                  <MDBox
                    key={room.roomId}
                    sx={{
                      border: '1px solid #ffffff1f',
                      borderRadius: '6px',
                      padding: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '6px',
                      background: 'linear-gradient(160deg, #111d38 0%, #162848 55%, #0e1a2e 100%)'
                    }}
                  >
                    <MDTypography sx={{ fontSize: '12px', color: '#9bc2ff' }}>Room ID: {room.roomId}</MDTypography>
                    <MDTypography sx={{ fontSize: '12px', color: '#fff' }}>Players: {room.players ?? 0}</MDTypography>
                    <MDTypography sx={{ fontSize: '12px', color: '#fff' }}>Pot: {room.pot ?? 0}</MDTypography>
                    <MDTypography sx={{ fontSize: '12px', color: '#fff' }}>
                      Status: {room.status || 'UNKNOWN'}
                    </MDTypography>
                    <MDButton
                      color='error'
                      size='small'
                      disabled={stopTarget === room.roomId}
                      onClick={() => handleStopRoom(room.roomId)}
                    >
                      {stopTarget === room.roomId ? 'Stopping...' : 'Stop'}
                    </MDButton>
                    <SimulateBlockMined
                      roomId={room.roomId}
                      exchangeData={exchangeData}
                    />
                  </MDBox>
                ))}
            </MDBox>
          </Can>
        )}
        {errorMessage && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#e71616' }}>
            Max Bet must be greater than Min Bet.
          </MDBox>
        )}
        {requestError && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#ff8a80' }}>
            {requestError}
          </MDBox>
        )}
        {updated && (
          <MDBox sx={{ fontSize: '12px', textAlign: 'center', color: '#fff' }}>Bet limit updated successfully.</MDBox>
        )}
        {gameType === 'nicehash-game' && (
          <MDButton
            color='success'
            sx={{ width: '100%', marginTop: '10px' }}
            onClick={() => navigate(`/nh-metrics/bet-limit/${id}`)}
          >
            View NH Metrics
          </MDButton>
        )}
        <MDBox sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', marginTop: '10px', gap: '8px' }}>
          <MDButton
            color='info'
            sx={{ width: '50%' }}
            disabled={gameType === 'nicehash-game' && !percentageSumValid}
            onClick={() => {
              if (Number(editBetLimitMin) >= Number(editBetLimitMax) && singleBet === null) {
                setErrorMessage(true);
              } else {
                setErrorMessage(false);
                updateBetLimitBtn();
              }
            }}
          >
            Update
          </MDButton>
          <MDButton color='error' sx={{ width: '50%' }} onClick={handleOpenDeleteBetLimit}>
            Delete
          </MDButton>
        </MDBox>
      </MDBox>

      <Dialog open={openDeleteBetLimit} onClose={handleCloseDeleteBetLimit} aria-labelledby='alert-dialog-title'>
        <DialogTitle id='alert-dialog-title' sx={{ textAlign: 'center' }}>
          Are you sure you want to delete this bet limit?
        </DialogTitle>
        <DialogActions>
          <MDButton
            variant='text'
            color='error'
            disabled={deleteLoading}
            onClick={handleConfirmDeleteBetLimit}
          >
            {deleteLoading ? 'Deleting...' : 'Yes'}
          </MDButton>
          <MDButton variant='text' disabled={deleteLoading} onClick={handleCloseDeleteBetLimit}>
            No
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}
